/**
 * User-context for the chat agent.
 *
 * Gives the agent a sense of *who* it's helping and *when they were last here*,
 * so it can personalize and recognize a returning user. Loaded once after
 * sign-in from `GET /api/auth/me` (which now returns the user's prior sign-in,
 * most-recent activity, and department for both DB and Entra auth), cached in
 * memory, and read synchronously when assembling each system prompt.
 *
 * The recency values are "previous-session": `/auth/me` is fetched at app load
 * before any chatting, so `lastActive` reflects the prior session, and
 * `previousLogin` is the value captured before this session's login bump.
 *
 * Best-effort throughout: a failed load just yields an empty note — agent
 * personalization is never allowed to break chat.
 */

import { getBackendUrl } from '@/config/runtimeConfig';
import { getBackendHeaders } from './backendAuth';

export interface UserContext {
  name?: string;
  department?: string;
  previousLogin?: string;
  lastActive?: string;
}

let cached: UserContext = {};

/**
 * Fetch + cache the user context. `displayName`/`department` come from the
 * already-resolved auth user (the most reliable display name); the prior-login
 * and last-active timestamps (and department fallback) come from `/auth/me`.
 */
export async function loadUserContext(
  displayName?: string,
  department?: string
): Promise<void> {
  const next: UserContext = {};
  if (displayName) next.name = displayName;
  if (department) next.department = department;

  try {
    const res = await fetch(`${getBackendUrl()}/auth/me`, {
      headers: await getBackendHeaders(),
    });
    if (res.ok) {
      const data = (await res.json()) as {
        previousLogin?: string | null;
        lastActive?: string | null;
        department?: string | null;
        username?: string | null;
      };
      if (data.previousLogin) next.previousLogin = data.previousLogin;
      if (data.lastActive) next.lastActive = data.lastActive;
      if (!next.department && data.department) next.department = data.department;
      if (!next.name && data.username) next.name = data.username;
    }
  } catch {
    // non-critical — personalization is best-effort.
  }

  cached = next;
}

/** Clear the cache (e.g. on sign-out / user switch). */
export function clearUserContext(): void {
  cached = {};
}

function formatWhen(iso?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(d);
}

/**
 * Build the one-paragraph user note prepended to the system prompt. Returns ''
 * when nothing is known (so it can be filtered out). Accepts an explicit
 * context for testing; defaults to the module cache.
 */
export function buildUserContextNote(ctx: UserContext = cached): string {
  const parts: string[] = [];

  if (ctx.name && ctx.department) {
    parts.push(
      `You are assisting ${ctx.name} from the ${ctx.department} department.`
    );
  } else if (ctx.name) {
    parts.push(`You are assisting ${ctx.name}.`);
  } else if (ctx.department) {
    parts.push(`You are assisting a user from the ${ctx.department} department.`);
  }

  const prev = formatWhen(ctx.previousLogin);
  if (prev) parts.push(`They last signed in on ${prev}.`);

  const active = formatWhen(ctx.lastActive);
  if (active) parts.push(`Their most recent prior activity was on ${active}.`);

  return parts.join(' ');
}
